import { createFileRoute, Link } from "@tanstack/react-router";
import { BrasilHub } from "@/components/match/brasil/BrasilHub";
import { COUNTRY_EXPERIENCES } from "@/lib/i18n/countryExperience";
import { listInfluencersByCountry } from "@/lib/growth/influencerRegistry";
import { WhatsAppGrowthCTAs } from "@/components/growth/WhatsAppGrowthCTAs";
import { TelegramCtaCard } from "@/components/growth/TelegramCtaCard";
import { BrasilShareEngine } from "@/components/growth/BrasilShareEngine";
import { BrasilHallOfFame } from "@/components/growth/BrasilHallOfFame";
import { BrasilRivalryCards } from "@/components/growth/BrasilRivalryCards";
import { BrasilShareCenter } from "@/components/growth/BrasilShareCenter";
import { BrasilCommunityFeed } from "@/components/growth/BrasilCommunityFeed";

export const Route = createFileRoute("/brasil/")({
  head: () => ({
    meta: [
      { title: "VerseSport Brasil — A segunda tela oficial da torcida brasileira" },
      {
        name: "description",
        content:
          "Estatísticas ao vivo, previsões, Nation Wars e segunda tela para a torcida brasileira. Funciona com CazéTV, Globo, SporTV, N Sports e Globoplay.",
      },
      { property: "og:title", content: "VerseSport Brasil — Segunda tela do futebol brasileiro" },
      {
        property: "og:description",
        content:
          "A casa digital da torcida brasileira no Mundial: previsões, ranking, Audio Sync e estatísticas ao vivo.",
      },
      { property: "og:url", content: "https://versesport.com/brasil" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://versesport.com/og/brasil.jpg" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://versesport.com/og/brasil.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://versesport.com/brasil" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "VerseSport Brasil",
          url: "https://versesport.com/brasil",
          logo: "https://versesport.com/og/brasil.jpg",
          sameAs: ["https://versesport.com"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsOrganization",
          name: "VerseSport Brasil — Torcida do Mundial",
          sport: "Football",
          url: "https://versesport.com/brasil",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "O VerseSport transmite os jogos do Mundial?",
              acceptedAnswer: { "@type": "Answer", text: "Não. VerseSport é segunda tela e acompanha a transmissão oficial que você já está assistindo (CazéTV, Globo, SporTV, N Sports, Globoplay)." },
            },
            {
              "@type": "Question",
              name: "Como funciona a Batalha das Torcidas?",
              acceptedAnswer: { "@type": "Answer", text: "Cada palpite, share e missão soma pontos para a sua torcida estadual e para o Brasil no ranking mundial." },
            },
            {
              "@type": "Question",
              name: "O Audio Sync evita spoilers?",
              acceptedAnswer: { "@type": "Answer", text: "Sim. O Audio Sync sincroniza o VerseSport com qualquer transmissão para você nunca receber alertas antes da jogada." },
            },
          ],
        }),
      },
    ],
  }),
  component: BrasilLanding,
});

function BrasilLanding() {
  const exp = COUNTRY_EXPERIENCES.BR;
  const influencers = listInfluencersByCountry("BR");
  return (
    <main className="mx-auto max-w-3xl space-y-4 px-3 py-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-black tracking-tight">{exp.brand.name}</h1>
        <p className="text-sm text-muted-foreground">{exp.brand.tagline}</p>
      </header>
      <BrasilHub />
      <BrasilHallOfFame />
      <BrasilRivalryCards />
      <BrasilShareCenter />
      <BrasilCommunityFeed />
      <BrasilShareEngine />
      <WhatsAppGrowthCTAs nationCode="BR" />
      <TelegramCtaCard country="BR" />
      <nav aria-label="Transmissões" className="rounded-2xl border border-border bg-background/60 p-3">
        <div className="mb-1 text-[11px] font-black uppercase tracking-widest text-primary">
          Páginas por transmissão
        </div>
        <ul className="flex flex-wrap gap-2 text-[12px] font-bold">
          <li><Link to="/brasil/cazetv" className="rounded-full border border-border px-3 py-1 hover:bg-accent">CazéTV</Link></li>
          <li><Link to="/brasil/globo" className="rounded-full border border-border px-3 py-1 hover:bg-accent">TV Globo</Link></li>
          <li><Link to="/brasil/sportv" className="rounded-full border border-border px-3 py-1 hover:bg-accent">SporTV</Link></li>
          <li><Link to="/brasil/n-sports" className="rounded-full border border-border px-3 py-1 hover:bg-accent">N Sports</Link></li>
          <li><Link to="/brasil/globoplay" className="rounded-full border border-border px-3 py-1 hover:bg-accent">Globoplay</Link></li>
        </ul>
      </nav>
      {influencers.length > 0 && (
        <nav aria-label="Influencers" className="rounded-2xl border border-border bg-background/60 p-3">
          <div className="mb-1 text-[11px] font-black uppercase tracking-widest text-primary">
            Torcidas de influencers
          </div>
          <ul className="flex flex-wrap gap-2 text-[12px] font-bold">
            {influencers.map((i) => (
              <li key={i.slug}>
                <Link
                  to="/brasil/influencer/$slug"
                  params={{ slug: i.slug }}
                  className="rounded-full border border-border px-3 py-1 hover:bg-accent"
                >
                  {i.emoji} {i.displayName}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <p className="px-1 text-[10px] leading-snug text-muted-foreground">
        VerseSport não transmite nem redistribui sinal. Acompanhamos a transmissão oficial que você
        já está assistindo. Marcas mencionadas pertencem a seus respectivos titulares.
      </p>
    </main>
  );
}
